import { create } from "zustand";

import { getPlatform } from "../platform/platformRef";

/**
 * Rolling journal of the admin's recent manual annotation operations, scoped
 * per document. The admin-chat panel snapshots it into the run's `ui_context`
 * so the agent can generalize "the last change" ("apply that to all M*
 * sheets"). Capacity is capped per document; entries are newest-first.
 *
 * Non-React write paths (save flushes, destination-panel mutations) record via
 * `recordAdminJournalOp` / `useAdminOpsJournal.getState()`.
 *
 * Entries are real per-tenant annotation history, so the map key carries the
 * current org slug — a document id colliding across tenant databases must never surface another
 * tenant's journal to admin-chat's `ui_context`. Tenant-scoped keys mean a
 * switch needs no explicit clear (an old org's entries are simply
 * unreachable under the new org's key, same reasoning as the file cache in
 * `TenantQueryClientProvider.tsx`); sign-out still clears the store outright
 * via `clearAdminOpsJournal()` (wired in `utils/logout.ts`) since it is
 * data-at-rest on a shared machine until then.
 */

export const ADMIN_OPS_JOURNAL_CAP = 20;

export interface AdminJournalOp {
    /** ISO timestamp. */
    ts: string;
    /** Machine-readable operation kind, e.g. "connect_tag", "set_destination". */
    kind: string;
    /** Human-readable one-liner, e.g. 'Connected tag "L5" to DOOR on A-101'. */
    description: string;
    /** Real (persisted) ids and values the agent can act on — never temp ids. */
    params?: Record<string, unknown>;
    groupCode?: string;
    page?: number;
}

interface AdminOpsJournalState {
    /** Newest-first per org-scoped document key, capped at ADMIN_OPS_JOURNAL_CAP. */
    opsByDocument: Record<string, AdminJournalOp[]>;
    record: (documentId: string, op: AdminJournalOp) => void;
    listForDocument: (documentId: string) => AdminJournalOp[];
    /** Sign-out: drop every tenant's journal outright. */
    clear: () => void;
}

const EMPTY_OPS: AdminJournalOp[] = [];

/** Namespaces a document id by the current org so a colliding id across tenant
 *  databases can never surface another tenant's journal entries. */
function journalKey(documentId: string): string {
    const orgSlug = getPlatform().tenant.orgSlug();
    return orgSlug ? `${orgSlug}:${documentId}` : documentId;
}

export const useAdminOpsJournal = create<AdminOpsJournalState>()((set, get) => ({
    opsByDocument: {},

    record: (documentId, op) =>
        set((state) => {
            const key = journalKey(documentId);
            const existing = state.opsByDocument[key] ?? EMPTY_OPS;
            return {
                opsByDocument: {
                    ...state.opsByDocument,
                    [key]: [op, ...existing].slice(0, ADMIN_OPS_JOURNAL_CAP),
                },
            };
        }),

    listForDocument: (documentId) => get().opsByDocument[journalKey(documentId)] ?? EMPTY_OPS,

    clear: () => set({ opsByDocument: {} }),
}));

/** Convenience for non-React call sites (save flushes, mutation handlers). */
export function recordAdminJournalOp(documentId: string, op: AdminJournalOp): void {
    useAdminOpsJournal.getState().record(documentId, op);
}

/** Sign-out: drop every tenant's journal. See `utils/logout.ts`. */
export function clearAdminOpsJournal(): void {
    useAdminOpsJournal.getState().clear();
}
